import { useEffect } from "react";
import { useContent } from "@/lib/ContentContext";
import useReveal from "@/lib/useReveal";
import Nav from "@/components/site/Nav";
import Hero from "@/components/site/Hero";
import Trust from "@/components/site/Trust";
import Why from "@/components/site/Why";
import Services from "@/components/site/Services";
import Founder from "@/components/site/Founder";
import Gallery from "@/components/site/Gallery";
import Process from "@/components/site/Process";
import Planning from "@/components/site/Planning";
import Awards from "@/components/site/Awards";
import Testimonials from "@/components/site/Testimonials";
import Contact from "@/components/site/Contact";
import FinalCTA from "@/components/site/FinalCTA";
import Footer from "@/components/site/Footer";
import StickyCTA from "@/components/site/StickyCTA";

export default function Home() {
  const { content, gallery, testimonials, awards, loading } = useContent();
  useReveal([loading]);

  // Dynamic SEO + favicon — kept in sync with whatever's in the CMS so admins
  // can change the brand logo / metadata without a redeploy.
  useEffect(() => {
    const seo = content?.seo;
    const brand = content?.brand;

    if (seo?.title) document.title = seo.title;

    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let m = document.querySelector(selector);
      if (!m) {
        m = document.createElement("meta");
        const [, key, name] = selector.match(/\[([^=]+)="([^"]+)"\]/) || [];
        if (key && name) m.setAttribute(key, name);
        document.head.appendChild(m);
      }
      m.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", seo?.description);
    setMeta('meta[property="og:title"]', "content", seo?.title);
    setMeta('meta[property="og:description"]', "content", seo?.description);
    setMeta('meta[name="twitter:title"]', "content", seo?.title);
    setMeta('meta[name="twitter:description"]', "content", seo?.description);
    if (seo?.og_image) {
      setMeta('meta[property="og:image"]', "content", seo.og_image);
      setMeta('meta[name="twitter:image"]', "content", seo.og_image);
    }

    // Favicon: derive sized variants from brand.logo_url via Cloudinary transforms
    if (brand?.logo_url && brand.logo_url.includes("res.cloudinary.com")) {
      const transformed = (w, h, bg = "transparent") =>
        brand.logo_url.replace(
          "/upload/",
          `/upload/w_${w},h_${h},c_pad,b_${bg},f_png/`,
        );
      const setIcon = (rel, sizes, href) => {
        const sel = sizes
          ? `link[rel="${rel}"][sizes="${sizes}"]`
          : `link[rel="${rel}"]`;
        let link = document.querySelector(sel);
        if (!link) {
          link = document.createElement("link");
          link.setAttribute("rel", rel);
          if (sizes) link.setAttribute("sizes", sizes);
          link.setAttribute("type", "image/png");
          document.head.appendChild(link);
        }
        link.setAttribute("href", href);
      };
      setIcon("icon", "16x16", transformed(16, 16));
      setIcon("icon", "32x32", transformed(32, 32));
      setIcon("icon", "48x48", transformed(48, 48));
      setIcon("icon", "192x192", transformed(192, 192));
      setIcon("apple-touch-icon", "180x180", transformed(180, 180, "white"));
    }
  }, [content]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <div className="font-display text-2xl text-charcoal/40 animate-pulse">Weddings by Mostash</div>
      </div>
    );
  }

  return (
    <div data-testid="site-home">
      <Nav brand={content?.brand} instagramUrl={content?.footer?.socials?.instagram} />
      <main>
        <Hero data={content?.hero} brand={content?.brand} />
        <Trust data={content?.trust} />
        <Why data={content?.why} />
        <Services data={content?.services} />
        <Founder data={content?.founder} />
        <Gallery section={content?.gallery_section} items={gallery} />
        <Process data={content?.process} />
        <Planning data={content?.planning_experiences} />
        <Testimonials section={content?.testimonials_section} items={testimonials} />
        <Awards data={content?.awards} items={awards} />
        <Contact data={content?.contact} footer={content?.footer} />
        <FinalCTA data={content?.final_cta} footer={content?.footer} />
      </main>
      <Footer brand={content?.brand} footer={content?.footer} />
      <StickyCTA footer={content?.footer} />
    </div>
  );
}
